'use client';

import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import {
  MOCK_CUSTOMERS, FARMS_LIST, PAYMENT_METHODS, SALE_TYPE_LABELS,
  SaleLineItem, SaleType, formatUGX,
} from './salesData';

interface Props {
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => void;
}

const EMPTY_LINE: SaleLineItem = {
  id: '', productType: 'eggs', description: '', quantity: 1, unit: 'trays', unitPrice: 0, totalPrice: 0,
};

export default function SaleInvoiceForm({ onClose, onSave }: Props) {
  const [farm, setFarm] = useState('');
  const [branch, setBranch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<SaleLineItem[]>([{ ...EMPTY_LINE, id: 'li-new-1' }]);

  const selectedFarm = FARMS_LIST.find(f => f.name === farm);
  const selectedCustomer = MOCK_CUSTOMERS.find(c => c.id === customerId);

  function handleCustomerChange(id: string) {
    setCustomerId(id);
    const c = MOCK_CUSTOMERS.find(c => c.id === id);
    if (c) {
      setCustomerName(c.name);
      setCustomerPhone(c.phone);
      setCustomerAddress(c.address);
    }
  }

  function updateLine(idx: number, field: keyof SaleLineItem, val: string | number) {
    setLineItems(prev => {
      const updated = [...prev];
      const line = { ...updated[idx], [field]: val };
      if (field === 'quantity' || field === 'unitPrice') {
        line.totalPrice = Number(line.quantity) * Number(line.unitPrice);
      }
      updated[idx] = line;
      return updated;
    });
  }

  function addLine() {
    setLineItems(prev => [...prev, { ...EMPTY_LINE, id: `li-new-${Date.now()}` }]);
  }

  function removeLine(idx: number) {
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  }

  const subtotal = lineItems.reduce((s, l) => s + l.totalPrice, 0);
  const total = subtotal - discount;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({ farm, branch, customerId, customerName, date, dueDate, lineItems, subtotal, discount, totalAmount: total, paymentMethod, notes });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">New Sale / Invoice</h2>
            <p className="text-xs text-gray-500 mt-0.5">Record a sale and generate an invoice</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Farm / Branch Scope */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Farm <span className="text-red-500">*</span></label>
              <select required value={farm} onChange={e => { setFarm(e.target.value); setBranch(''); }}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Select farm</option>
                {FARMS_LIST.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Branch</label>
              <select value={branch} onChange={e => setBranch(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">All branches</option>
                {(selectedFarm?.branches ?? []).map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          {/* Customer */}
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50 space-y-3">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Customer</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Select Existing Customer</label>
                <select value={customerId} onChange={e => handleCustomerChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— New / Walk-in Customer —</option>
                  {MOCK_CUSTOMERS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Customer Name <span className="text-red-500">*</span></label>
                <input required value={customerName} onChange={e => setCustomerName(e.target.value)}
                  placeholder="Full name or business name"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone</label>
                <input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)}
                  placeholder="+256 7XX XXX XXX"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Address</label>
                <input value={customerAddress} onChange={e => setCustomerAddress(e.target.value)}
                  placeholder="Town / market / address"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            {selectedCustomer && selectedCustomer.outstandingBalance > 0 && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
                <span className="font-semibold">Outstanding balance:</span> {formatUGX(selectedCustomer.outstandingBalance)} from previous invoices
              </div>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Invoice Date <span className="text-red-500">*</span></label>
              <input required type="date" value={date} onChange={e => setDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Due Date</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Payment Method</label>
              <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Line Items</p>
              <button type="button" onClick={addLine}
                className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                <Plus size={13} /> Add Item
              </button>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">Product</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">Description</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-600">Qty</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600">Unit</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-600">Unit Price</th>
                    <th className="text-right px-3 py-2 font-semibold text-gray-600">Total</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lineItems.map((line, idx) => (
                    <tr key={line.id} className="bg-white">
                      <td className="px-2 py-1.5">
                        <select value={line.productType} onChange={e => updateLine(idx, 'productType', e.target.value as SaleType)}
                          className="border border-gray-200 rounded-md px-2 py-1 text-xs w-28 focus:outline-none focus:ring-1 focus:ring-blue-500">
                          {Object.entries(SALE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={line.description} onChange={e => updateLine(idx, 'description', e.target.value)}
                          placeholder="Description"
                          className="border border-gray-200 rounded-md px-2 py-1 text-xs w-36 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" min={1} value={line.quantity} onChange={e => updateLine(idx, 'quantity', Number(e.target.value))}
                          className="border border-gray-200 rounded-md px-2 py-1 text-xs w-16 text-right focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input value={line.unit} onChange={e => updateLine(idx, 'unit', e.target.value)}
                          placeholder="unit"
                          className="border border-gray-200 rounded-md px-2 py-1 text-xs w-16 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="number" min={0} value={line.unitPrice} onChange={e => updateLine(idx, 'unitPrice', Number(e.target.value))}
                          className="border border-gray-200 rounded-md px-2 py-1 text-xs w-24 text-right focus:outline-none focus:ring-1 focus:ring-blue-500" />
                      </td>
                      <td className="px-3 py-1.5 text-right font-semibold text-gray-800">
                        {line.totalPrice.toLocaleString()}
                      </td>
                      <td className="px-2 py-1.5">
                        {lineItems.length > 1 && (
                          <button type="button" onClick={() => removeLine(idx)} className="text-gray-300 hover:text-red-500 transition-colors">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span className="font-medium">{formatUGX(subtotal)}</span>
              </div>
              <div className="flex justify-between items-center text-gray-600">
                <span>Discount</span>
                <input type="number" min={0} value={discount} onChange={e => setDiscount(Number(e.target.value))}
                  className="w-28 border border-gray-200 rounded-md px-2 py-1 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-2">
                <span>Total</span>
                <span className="text-blue-700">{formatUGX(total)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="Payment terms, delivery notes, etc."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors">
            Cancel
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={() => { onSave({ draft: true }); onClose(); }}
              className="px-4 py-2 text-sm font-semibold border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors">
              Save as Draft
            </button>
            <button type="submit" form="sale-form" onClick={(e) => { e.preventDefault(); const form = document.querySelector('form'); form?.requestSubmit(); }}
              className="px-5 py-2 text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-sm">
              Create Invoice
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
